import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import {
  useTenants,
  useCreateTenant,
  useUpdateTenant,
} from "../../hooks/admin/use-tenants";
import { HorizontalScrollTable } from "@/components/molecules/HorizontalScrollTable";

type EmailTemplateType = "standard" | "sothebys" | "wallace";

export function AdminTenants() {
  const { data: tenants, isLoading } = useTenants();
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    templateId: "wallace" as EmailTemplateType,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createTenant.mutateAsync(formData);
    setFormData({ name: "", slug: "", templateId: "wallace" });
    setShowForm(false);
  };

  const handleTemplateChange = async (
    tenantId: string,
    templateId: EmailTemplateType,
  ) => {
    await updateTenant.mutateAsync({ id: tenantId, data: { templateId } });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Tenants
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage organization tenants
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "Add Tenant"}
        </Button>
      </Box>

      {showForm && (
        <Card
          component="form"
          onSubmit={handleSubmit}
          variant="outlined"
          sx={{ p: 3, mb: 3 }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                inputProps={{ pattern: "^[a-z0-9-]+$" }}
                helperText="Lowercase letters, numbers, and hyphens only"
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="template-select-label">
                  Email Template
                </InputLabel>
                <Select
                  labelId="template-select-label"
                  label="Email Template"
                  value={formData.templateId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      templateId: e.target.value as EmailTemplateType,
                    })
                  }
                >
                  <MenuItem value="wallace">Wallace Real Estate</MenuItem>
                  <MenuItem value="sothebys">Sotheby&apos;s</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={createTenant.isPending}
            sx={{ mt: 2 }}
          >
            {createTenant.isPending ? "Creating..." : "Create Tenant"}
          </Button>
        </Card>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={80} />
          ))}
        </Box>
      ) : (
        <Card variant="outlined" sx={{ overflow: "hidden" }}>
          <HorizontalScrollTable minWidth={650}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants?.map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {tenant.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tenant.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={tenant.templateId}
                        onChange={(e) =>
                          handleTemplateChange(
                            tenant.id,
                            e.target.value as EmailTemplateType,
                          )
                        }
                        disabled={updateTenant.isPending}
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="wallace">Wallace Real Estate</MenuItem>
                        <MenuItem value="sothebys">Sotheby&apos;s</MenuItem>
                        <MenuItem value="standard">Standard</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.isActive ? "Active" : "Inactive"}
                        color={tenant.isActive ? "success" : "error"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(tenant.createdAt).toLocaleDateString("en-NZ")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </HorizontalScrollTable>
        </Card>
      )}
    </Box>
  );
}
